import React from "react";
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import MobileBottomSheet from "@/components/ui/MobileBottomSheet";
import { Shield, Lock, Eye, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * SecurityModal - Modal showing LGPD compliance and data security information
 * Accessible from the main header
 */
export const SecurityModal = ({ open, onOpenChange }) => {
  const platforms = [
    {
      name: "WhatsApp",
      color: "bg-green-50 border-green-200",
      iconColor: "text-green-600",
      items: [
        { icon: Lock, text: "Criptografia de ponta a ponta (E2EE)" },
        { icon: Eye, text: "Não armazenamos suas mensagens após processamento" },
        { icon: Shield, text: "Conexão via QR Code ou API oficial Meta" },
        { icon: Trash2, text: "Você pode desconectar a qualquer momento" },
      ],
    },
    {
      name: "Instagram",
      color: "bg-purple-50 border-purple-200",
      iconColor: "text-purple-600",
      items: [
        { icon: Lock, text: "Autenticação OAuth2 segura via Meta" },
        { icon: Eye, text: "Solicitamos apenas permissões necessárias" },
        { icon: Shield, text: "Não postamos sem sua autorização" },
        { icon: Trash2, text: "Tokens com expiração automática" },
      ],
    },
    {
      name: "Google Calendar",
      color: "bg-blue-50 border-blue-200",
      iconColor: "text-blue-600",
      items: [
        { icon: Lock, text: "Autenticação OAuth2 oficial Google" },
        { icon: Eye, text: "Acesso somente leitura ao calendário" },
        { icon: Shield, text: "Não modificamos seus eventos" },
        { icon: Trash2, text: "Revogue o acesso a qualquer momento" },
      ],
    },
  ];

  return (
    <MobileBottomSheet open={open} onOpenChange={onOpenChange} className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="h-5 w-5 text-emerald-600" />
            Seus dados estão seguros
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">
              <Shield className="h-3 w-3" />
              LGPD
            </span>
          </DialogTitle>
          <DialogDescription>
            O SacGPT segue as melhores práticas de segurança e está em conformidade com a Lei Geral de Proteção de Dados (LGPD).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {platforms.map((platform) => (
            <div
              key={platform.name}
              className={cn("rounded-lg border p-4", platform.color)}
            >
              <h3 className={cn("font-semibold mb-3", platform.iconColor)}>
                {platform.name}
              </h3>
              <ul className="space-y-2">
                {platform.items.map((item, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-slate-700">
                    <item.icon className={cn("h-3.5 w-3.5 flex-shrink-0", platform.iconColor)} />
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* General Info */}
          <div className="rounded-lg border bg-slate-50 border-slate-200 p-4">
            <h3 className="font-semibold mb-3 text-slate-700">
              Informações Gerais
            </h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-slate-600">
                <Lock className="h-3.5 w-3.5 flex-shrink-0 text-slate-500" />
                <span>Todas as conexões são criptografadas (HTTPS/TLS)</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-600">
                <Eye className="h-3.5 w-3.5 flex-shrink-0 text-slate-500" />
                <span>Não vendemos ou compartilhamos seus dados com terceiros</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-600">
                <Shield className="h-3.5 w-3.5 flex-shrink-0 text-slate-500" />
                <span>Servidores protegidos com as melhores práticas de segurança</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-600">
                <Trash2 className="h-3.5 w-3.5 flex-shrink-0 text-slate-500" />
                <span>Você pode solicitar a exclusão dos seus dados a qualquer momento</span>
              </li>
            </ul>
          </div>

        </div>
    </MobileBottomSheet>
  );
};

export default SecurityModal;
